import { AxiosError } from 'axios';
import { MY_ACCOUNT_LOGIN_URL } from 'features/my-account/routes.roc';
import React, { useEffect, useRef, useState } from 'react';
import { Localizer } from 'services/Localizer';
import { getUserData } from 'services/UserData';
import { AddToListResponse } from 'services/UserData.Commerce';
import Modal from 'shared/components/Modal';
import Spinner from 'shared/components/Spinner';
import CheckCircleSVG from 'shared/components/svg/CheckCircleSVG';
import InlineAlert from 'shared/edit/InlineAlert';
import ShoppingListEditItem from '../my-account/shopping-lists/ShoppingListEditItem';
import { PrivacyType, PrivacyTypeOptions, ShoppingListDto } from '../my-account/shopping-lists/types';
import { useShoppingLists } from '../my-account/shopping-lists/useShoppingLists';
import AddedToListAlert from '../store/AddedToListAlert';
import ProductAddToListModal from './ProductAddToListModal';
import { useProductDetailsContext } from './ProductDetailsContext';
import { ProductAddOnDto, ProductType, Summary } from './types';
import { UseProductDetails } from './useProductDetails';

interface ProductAddToListProps {
	summary?: Summary;
	id: string;
	isUserSignedIn: boolean;
	quantity: number | null;
	selectedProductPricingLineId: string | null;
	addOns?: ProductAddOnDto[];
	addOnsHandleSubmitWithError?: (error: AxiosError) => void;
}

/**
 * Represents the component on a product details page that adding a product to the user's shopping list.
 */
export default function ProductAddToList(props: ProductAddToListProps) {
	const UseProductDetailsResult: UseProductDetails = useProductDetailsContext();

	const {
		state: { productDetails, selectedPricingLine },
		actor: { isPurchaseable },
	} = UseProductDetailsResult;

	const [displaySelectionsErrorMessage, setDisplaySelectionsErrorMessage] = useState<boolean>(false);
	const [openListModal, setOpenListModal] = useState<boolean>(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [addToListResponse, setAddToListResponse] = useState<AddToListResponse | null>(null);
	const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);
	const [addingToList, setAddingToList] = useState(false);

	const triggerRef = useRef<HTMLButtonElement | null>(null);

	const {
		state: { isSaving, shoppingListBeingEdited, shoppingList, error, errorMessage },
		actor: { stopEditingShoppingList, saveShoppingList, beginCreateShoppingList, addProductToList },
	} = useShoppingLists();

	const allSelectionsSelected = props.summary && props.summary.type === ProductType.Simple;

	const cancelEditShoppingList = () => {
		stopEditingShoppingList();
		setModalOpen(false);
	};

	const onSaveShoppingList = async (formValues: ShoppingListDto) => {
		const validationResult = await saveShoppingList(formValues);
		if (validationResult) {
			return validationResult;
		} else {
			setModalOpen(false);
			return;
		}
	};

	const handleListModal = async () => {
		if (!allSelectionsSelected) {
			setDisplaySelectionsErrorMessage(true);
			return;
		}

		setDisplaySelectionsErrorMessage(false);

		if (props.isUserSignedIn) {
			if (openListModal) {
				setOpenListModal(false);
			} else {
				setOpenListModal(true);
			}
		} else {
			window.location.href =
				MY_ACCOUNT_LOGIN_URL + '?ReturnUrl=' + encodeURIComponent(props.summary?.productUrl ?? '');
		}
	};

	const handleAddToList = async (shoppingListId) => {
		setConfirmationPopupOpen(false);

		try {
			console.debug('Adding to list:', props.id, shoppingListId);
			setOpenListModal(false);
			setAddingToList(true);

			const response = await addProductToList({
				itemId: props.id,
				quantity: props.quantity ?? 1,
				shoppingListId: shoppingListId,
				productPricingLineId: props.selectedProductPricingLineId,
				addOns: props.addOns,
			});

			setConfirmationPopupOpen(true);

			if (!response) {
				setAddToListResponse(null);
				return;
			}

			setAddToListResponse(response);
		} catch (error) {
			setAddToListResponse(null);

			const axiosError: AxiosError = error;
			if (axiosError && axiosError.response && axiosError.response.status === 400 && axiosError.response.data) {
				//notify the add ons form with the validation errors
				if (props.addOnsHandleSubmitWithError) {
					props.addOnsHandleSubmitWithError(axiosError);
				}
			}
		}

		setAddingToList(false);
		return;
	};

	useEffect(() => {
		if (allSelectionsSelected && displaySelectionsErrorMessage) {
			setDisplaySelectionsErrorMessage(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.summary, displaySelectionsErrorMessage]);

	useEffect(() => {
		(async () => {
			try {
				const data = (await getUserData()).data;

				if (!data?.summary?.organization) {
					const organizationOption = PrivacyTypeOptions.find((s) => s.text === PrivacyType.Organization);

					if (organizationOption != undefined) {
						PrivacyTypeOptions.splice(PrivacyTypeOptions.indexOf(organizationOption));
					}
				}
			} catch (error) {
				console.error('Unable to load user data.', error);
			}
		})();

		if (!shoppingListBeingEdited) {
			beginCreateShoppingList();
		}
	}, [shoppingListBeingEdited, beginCreateShoppingList]);

	useEffect(() => {
		if (shoppingList) {
			handleAddToList(shoppingList.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shoppingList]);

	return (
		<>
			<button
				onClick={() => handleListModal()}
				type="button"
				className="roc-btn roc-btn--big"
				disabled={
					addingToList ||
					props.quantity == null ||
					!isPurchaseable(productDetails.availability, selectedPricingLine.pricingLine)
				}
				data-testid="add-to-list-button"
				ref={triggerRef}
			>
				{Localizer('AddToList')}
				{addingToList && <Spinner />}
			</button>

			{addToListResponse !== null ? (
				<Modal
					isOpen={confirmationPopupOpen}
					renderTitle={() => Localizer('AddedToList')}
					closeModal={() => setConfirmationPopupOpen(false)}
					titleIcon={<CheckCircleSVG />}
				>
					<AddedToListAlert response={addToListResponse} setPopupOpen={setConfirmationPopupOpen} />
				</Modal>
			) : null}

			{openListModal ? (
				<ProductAddToListModal
					openListModal={openListModal}
					setModalOpen={setModalOpen}
					setOpenListModal={setOpenListModal}
					handleAddToList={handleAddToList}
				/>
			) : null}

			<Modal
				isOpen={modalOpen}
				renderTitle={() => Localizer('AddNewShoppingList')}
				closeModal={() => setModalOpen(false)}
			>
				<ShoppingListEditItem
					stopEditingShoppingList={cancelEditShoppingList}
					shoppingListBeingEdited={shoppingListBeingEdited || ({} as ShoppingListDto)}
					onSaveShoppingList={onSaveShoppingList}
					isSaving={isSaving}
					shoppingList={[]}
				/>
			</Modal>

			{error != null || errorMessage ? (
				<InlineAlert
					theme="danger"
					message={errorMessage ?? Localizer('ErrorWhenAddingToList')}
					error={error}
				/>
			) : null}

			{displaySelectionsErrorMessage ? (
				<InlineAlert
					theme="danger"
					title=""
					isTitleOnOwnRow
					message={Localizer('AddToListSelectionErrorMessage')}
					error={null}
				/>
			) : null}
		</>
	);
}
